// Top navigation bar — page links (with priority+ overflow menu), email badge,
// alert badge, conversation history/export, theme toggle.
//
// Priority+ nav: 11 labeled items don't fit a top bar at laptop widths
// (1280–1440). Rather than clip/scroll, we measure the available track against a
// hidden ghost row (natural widths, ResizeObserver) and render only the items
// that fit; the remainder collapse into a "More ▾" dropdown that shows the
// active-underline when the current route lives inside it. Adapts to any width.
import { ChevronDown, Download, History, Keyboard, Moon, Monitor, Sun } from 'lucide-react';
import { NavLink, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useLayoutEffect, useRef, useState } from 'react';
import { Button } from '@onestone/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@onestone/ui/dropdown-menu';
import { useTheme } from '@/components/ThemeProvider';
import { chatStore, useChatStore } from '@/lib/chatStore';
import { api } from '@/lib/api';
import { isCloud } from '@/lib/surface';
import { cn } from '@onestone/ui/utils';
import { NAV_BADGE_POLL_MS } from '@/lib/uiConstants';
import { AccountMenu } from './AccountMenu';
import { ActiveProjectIndicator } from './ActiveProjectIndicator';
import { RestartButton } from './RestartButton';
import { NAV, type NavEntry } from './nav';

interface TopNavProps {
  onShowShortcuts?: () => void;
}

/** Count badge shared by the inline links and the overflow menu. */
function NavBadge({ count, tone }: { count: number; tone: 'primary' | 'destructive' }) {
  return (
    <span
      className={cn(
        'flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-xs font-semibold leading-none',
        tone === 'primary' ? 'bg-primary text-primary-foreground' : 'bg-destructive text-destructive-foreground',
      )}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

/** Icon + label + optional badge — shared by the visible links and the ghost
 *  measurement row so their widths match exactly. */
function NavInner({ entry, unreadCount, alertCount }: { entry: NavEntry; unreadCount: number; alertCount: number }) {
  const { label, icon: Icon } = entry;
  return (
    <>
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      <span>{label}</span>
      {label === 'Email' && unreadCount > 0 ? <NavBadge count={unreadCount} tone="primary" /> : null}
      {label === 'Analytics' && alertCount > 0 ? <NavBadge count={alertCount} tone="destructive" /> : null}
    </>
  );
}

export function TopNav({ onShowShortcuts }: TopNavProps) {
  const { theme, setTheme, resolvedTheme } = useTheme();
  const ThemeIcon = resolvedTheme === 'dark' ? Moon : Sun;
  const { messages, conversationId } = useChatStore();
  const exportDisabled = messages.length === 0;
  const historyDisabled = conversationId.length === 0;
  const location = useLocation();

  // Badge pollers hit desktop-server routes (/api/email/status, /api/alerts)
  // that don't exist on the Worker backend — disabled on cloud so the nav
  // isn't a background 404 generator (surface-parity Phase 2).
  const desktopBackend = !isCloud();
  const emailStatus = useQuery<{ configured: boolean; unreadCount?: number | null }>({
    queryKey: ['email-status'],
    queryFn: () => api('/api/email/status'),
    refetchInterval: NAV_BADGE_POLL_MS,
    retry: false,
    enabled: desktopBackend,
  });
  const unreadCount = emailStatus.data?.unreadCount ?? 0;

  const alertStatus = useQuery<{ count: number }>({
    queryKey: ['alert-status'],
    queryFn: () => api('/api/alerts'),
    refetchInterval: NAV_BADGE_POLL_MS,
    retry: false,
    enabled: desktopBackend,
  });
  const alertCount = alertStatus.data?.count ?? 0;

  // Priority+ overflow measurement.
  const navRef = useRef<HTMLElement>(null);
  const ghostRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(NAV.length);

  useLayoutEffect(() => {
    const navEl = navRef.current;
    const ghostEl = ghostRef.current;
    if (!navEl || !ghostEl) return;
    const RESERVE = 92; // room for the "More ▾" trigger when something overflows
    const GAP = 2; // gap-0.5
    const compute = () => {
      const avail = navEl.clientWidth;
      const widths = (Array.from(ghostEl.children) as HTMLElement[]).map((el) => el.offsetWidth);
      const total = widths.reduce((sum, w) => sum + w + GAP, 0);
      if (total <= avail) {
        setVisibleCount(NAV.length);
        return;
      }
      let used = 0;
      let count = 0;
      for (const w of widths) {
        used += w + GAP;
        if (used <= avail - RESERVE) count++;
        else break;
      }
      setVisibleCount(Math.max(count, 0));
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(navEl);
    return () => ro.disconnect();
  }, [unreadCount, alertCount]);

  const visible = NAV.slice(0, visibleCount);
  const overflow = NAV.slice(visibleCount);
  const overflowActive = overflow.some((e) => location.pathname.startsWith(e.to));

  return (
    <header
      data-testid="top-nav"
      className="sticky top-0 z-40 border-b border-border bg-background/90 backdrop-blur-md drag-region"
    >
      <div className="flex h-12 items-center px-4 no-drag">
        {/* Brand */}
        <div className="flex items-center gap-2.5 pr-4">
          <img src="./logo-256.png" alt="OneStone" className="h-7 w-7 rounded-lg shadow-sm" />
          <span className="text-sm font-bold tracking-tight">OneStoneAI</span>
        </div>

        {/* Nav links (priority+ — overflow collapses into "More") */}
        <nav ref={navRef} aria-label="Primary" className="relative flex h-full min-w-0 flex-1 items-center gap-0.5">
          {/* Ghost measurement row — invisible, natural width, never wraps. */}
          <div
            ref={ghostRef}
            aria-hidden="true"
            className="pointer-events-none invisible absolute left-0 top-0 flex w-max items-center gap-0.5"
          >
            {NAV.map((entry) => (
              <span key={entry.to} className="flex items-center gap-1 px-2.5 text-sm font-medium">
                <NavInner entry={entry} unreadCount={unreadCount} alertCount={alertCount} />
              </span>
            ))}
          </div>

          {visible.map((entry) => (
            <NavLink
              key={entry.to}
              to={entry.to}
              data-testid={`nav-link-${entry.to.replace('/', '')}`}
              className={({ isActive }) =>
                cn(
                  'relative flex h-full items-center gap-1 px-2.5 text-sm font-medium transition-colors',
                  isActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <NavInner entry={entry} unreadCount={unreadCount} alertCount={alertCount} />
                  {isActive ? <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-primary" /> : null}
                </>
              )}
            </NavLink>
          ))}

          {overflow.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  data-testid="nav-more"
                  aria-label="More navigation"
                  className={cn(
                    'relative flex h-full items-center gap-1 rounded-md px-2.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    overflowActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  <span>More</span>
                  <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                  {overflowActive ? (
                    <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-primary" />
                  ) : null}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {overflow.map((entry) => {
                  const Icon = entry.icon;
                  return (
                    <DropdownMenuItem key={entry.to} asChild>
                      <NavLink
                        to={entry.to}
                        className={({ isActive }) =>
                          cn('flex w-full cursor-pointer items-center gap-2', isActive && 'text-primary')
                        }
                      >
                        <Icon className="h-4 w-4" aria-hidden="true" />
                        <span>{entry.label}</span>
                        {entry.label === 'Email' && unreadCount > 0 ? (
                          <span className="ml-auto">
                            <NavBadge count={unreadCount} tone="primary" />
                          </span>
                        ) : null}
                        {entry.label === 'Analytics' && alertCount > 0 ? (
                          <span className="ml-auto">
                            <NavBadge count={alertCount} tone="destructive" />
                          </span>
                        ) : null}
                      </NavLink>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </nav>

        {/* Right actions */}
        <div className="ml-auto flex items-center gap-1">
          <ActiveProjectIndicator />
          <div className="mx-0.5 h-5 w-px bg-border" aria-hidden="true" />
          <RestartButton />
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            aria-label="Conversation history"
            title={historyDisabled ? 'No active conversation' : 'Browse past conversations'}
            disabled={historyDisabled}
            onClick={() => chatStore.setHistoryOpen(true)}
          >
            <History className="h-3.5 w-3.5" aria-hidden="true" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            aria-label="Export conversation"
            title={exportDisabled ? 'No messages to export' : 'Export conversation as JSON'}
            disabled={exportDisabled}
            onClick={() => void chatStore.exportCurrentConversation()}
          >
            <Download className="h-3.5 w-3.5" aria-hidden="true" />
          </Button>
          {onShowShortcuts && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              aria-label="Show keyboard shortcuts"
              title="Keyboard shortcuts (?)"
              onClick={onShowShortcuts}
            >
              <Keyboard className="h-3.5 w-3.5" aria-hidden="true" />
            </Button>
          )}
          {/* Theme toggle */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Toggle theme">
                <ThemeIcon className="h-3.5 w-3.5" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Theme</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => setTheme('light')}>
                <Sun className="mr-2 h-4 w-4" />
                Light
                {theme === 'light' && <span className="ml-auto text-xs">●</span>}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setTheme('dark')}>
                <Moon className="mr-2 h-4 w-4" />
                Dark
                {theme === 'dark' && <span className="ml-auto text-xs">●</span>}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setTheme('system')}>
                <Monitor className="mr-2 h-4 w-4" />
                System
                {theme === 'system' && <span className="ml-auto text-xs">●</span>}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {/* Account chip — cloud host only (null account → renders nothing) */}
          <AccountMenu />
        </div>
      </div>
    </header>
  );
}
